import React, { useEffect, useState } from 'react';
import Filters from '../components/Filters';
import { useData } from '../context/DataContext';
import Card from '../components/Card';
import { useFavorites } from '../context/FavoritesContext';
import Carousel from '../components/Carousel';
import TagsHoc from '../components/hoc/TagsHoc';
import { useQueryStrings } from '../context/QueryStringContext';
import Button from '../components/Button';
import Popup from '../components/Popup';
import Calendar from '../components/Calendar';
import { notFalsy, notUndefined, onlyUnique } from '../../../../../../../shared/lib/functional';
import { useGlobalControls } from '../context/GlobalControlsContext';
import {
  addDaysToDateLocal,
  createIsoDateList,
  dateToIsoLocal,
  getLocaleDateStringLocal,
  isoToDateLocal,
} from '../../../../../../../shared/lib/time';
import { isIsoDateInRange } from '../../../../../../../shared/lib/time';
import { useApp } from '../context/AppContext';
import { getOptimizedEdpImageUrl } from '../../../../../../../shared/lib/image';

function LineupPage() {
  const { dateLineup, todayLineup, data } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { qs, updateQs } = useQueryStrings();
  const { toggleOverflow } = useGlobalControls();
  const { config } = useApp();

  const [showCalendar, setShowCalendar] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [toggleImages, setToggleImages] = useState(true);
  const [search, setSearch] = useState('');

  const current = qs.date;
  const today = config.date;

  const currentDate = isoToDateLocal(current);
  const todayDate = isoToDateLocal(today);
  const tomorrowDate = addDaysToDateLocal(todayDate, 1);
  const prevDate = addDaysToDateLocal(currentDate, -1);
  const nextDate = addDaysToDateLocal(currentDate, 1);

  const tomorrow = dateToIsoLocal(tomorrowDate);

  const disablePrev = !isIsoDateInRange(
    dateToIsoLocal(prevDate),
    today,
    data.appConfig.FUTURE_DAY_CAP
  );
  const disableNext = !isIsoDateInRange(
    dateToIsoLocal(nextDate),
    today,
    data.appConfig.FUTURE_DAY_CAP
  );

  const filteredEvents = dateLineup.events.filter(event => {
    let filterCheck = true;

    activeFilters.forEach(filter => {
      switch (filter) {
        case data.appConfig.TRENDING_LABEL: {
          filterCheck = filterCheck && event.isTrending;
          break;
        }
        case data.appConfig.UPCOMING_LABEL: {
          filterCheck = filterCheck && event.isUpcoming;
          break;
        }
        default: {
          if (data.collections.attributes[filter]) {
            filterCheck =
              filterCheck &&
              (event.attributeIds.length === 0 || event.attributeIds.includes(filter));
          } else if (data.collections.zones[filter]) {
            filterCheck =
              filterCheck && (event.zoneIds.length === 0 || event.zoneIds.includes(filter));
          }
          break;
        }
      }
    });

    if (search.length > 0) {
      filterCheck = filterCheck && event.title.toLowerCase().includes(search.toLowerCase());
    }

    return filterCheck;
  });

  function handleDateChange(date: Date) {
    updateQs('date', dateToIsoLocal(date));
    setShowCalendar(false);
  }

  function handleDateIncrement(date: Date, direction: 1 | -1) {
    const newDate = addDaysToDateLocal(date, direction);
    if (!isIsoDateInRange(dateToIsoLocal(newDate), today, data.appConfig.FUTURE_DAY_CAP)) {
      return;
    }
    handleDateChange(newDate);
  }

  useEffect(() => {
    toggleOverflow('calendar', showCalendar);
  }, [showCalendar]);

  const zoneKeys = Object.keys(data.collections.zones).map(e => data.collections.zones[e]._id);

  const attributeKeys = dateLineup.events
    .map(e => e.attributeIds)
    .flat()
    .filter(notFalsy)
    .filter(onlyUnique);

  return (
    <div id="LineupPage">
      {showCalendar && (
        <Popup onClose={() => setShowCalendar(false)}>
          <Calendar
            className="w-full border-b border-slate-300"
            enabledDates={createIsoDateList(config.date, data.appConfig.FUTURE_DAY_CAP)}
            selectedDate={currentDate}
            onDateSelect={handleDateChange}
            showToday
          />
        </Popup>
      )}
      <Filters
        search={search}
        onSearch={setSearch}
        title={data.appConfig.LINEUP_LABEL}
        icon={['far', 'calendar-day']}
        staticButtons={[
          todayLineup.events.find(e => e.isTrending) ? data.appConfig.TRENDING_LABEL : undefined,
          todayLineup.date === dateLineup.date && todayLineup.events.find(e => e.isUpcoming)
            ? data.appConfig.UPCOMING_LABEL
            : undefined,
        ].filter(notUndefined)}
        keys={[...zoneKeys, ...attributeKeys]}
        onFilterChange={setActiveFilters}
        labelExtractor={(key: string) =>
          data.collections.attributes[key]?.name || data.collections.zones[key]?.name
        }
        rightSide={
          <div className="flex items-center -mr-4">
            <Button
              onClick={() => setToggleImages(!toggleImages)}
              icon={['fass', 'image']}
              className={`hidden ${toggleImages ? 'opacity-100' : 'opacity-50'}`}
            />
            <Button
              className={`font-semibold text-blue-500 py-3 px-4 -my-3 pr-0 date-picker ${
                disablePrev ? 'opacity-50 pointer-events-none' : ''
              }`}
              icon={['fass', 'arrow-left']}
              onClick={() => handleDateIncrement(currentDate, -1)}
              disabled={disablePrev}
            />
            <Button
              className="font-semibold text-blue-500 py-3 px-4 -my-3 date-picker"
              onClick={() => setShowCalendar(true)}
            >
              {current === today
                ? data.appConfig.TODAY_LABEL
                : current === tomorrow
                  ? data.appConfig.TOMORROW_LABEL
                  : getLocaleDateStringLocal(currentDate, {
                      month: 'short',
                      day: 'numeric',
                      weekday: 'short',
                    })}
            </Button>
            <Button
              className={`font-semibold text-blue-500 py-3 px-4 -my-3 pl-0 date-picker ${
                disableNext ? 'opacity-50 pointer-events-none' : ''
              }`}
              icon={['fass', 'arrow-right']}
              onClick={() => handleDateIncrement(currentDate, 1)}
              disabled={disableNext}
            />
          </div>
        }
      />
      <div className="grid gap-2 pb-8">
        {filteredEvents.map((evt, i) => {
          return (
            <TagsHoc type="event" item={evt} key={evt.id}>
              {props => (
                <>
                  <Card
                    className="border-b"
                    onClick={() => updateQs('event', evt.id)}
                    title={evt.title}
                    subtitle={[evt.venueString, evt.timeString]}
                    image={
                      toggleImages
                        ? getOptimizedEdpImageUrl(
                            evt.images,
                            evt.imageOptimizations,
                            config.imgRequirements,
                            0
                          )
                        : undefined
                    }
                    tags={props.tags}
                    isFavorite={!!favorites.events[evt.id]}
                    onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                  />
                  {i === 1 &&
                    activeFilters.length === 0 &&
                    todayLineup.offers.filter(o => o.isTrending).length > 0 &&
                    filteredEvents.length > 2 &&
                    config.enabledViews.includes('offers') && (
                      <Carousel
                        title="Special Offers"
                        icon={['far', 'hand-holding-circle-dollar']}
                        className="-my-2 carousel-container py-4"
                      >
                        {todayLineup.offers
                          .filter(o => o.isTrending)
                          .map(offer => (
                            <TagsHoc type="basic" item={offer} key={offer.id}>
                              {props => (
                                <Card
                                  className="carousel border"
                                  onClick={() => updateQs('offer', offer.id)}
                                  title={offer.title}
                                  subtitle={offer.description}
                                  image={getOptimizedEdpImageUrl(
                                    offer.images,
                                    offer.imageOptimizations,
                                    config.imgRequirements,
                                    0
                                  )}
                                  tags={props.tags}
                                  isFavorite={!!favorites.offers[offer.id]}
                                  onFavoriteClick={() =>
                                    toggleFavorite({ type: 'offers', value: offer })
                                  }
                                />
                              )}
                            </TagsHoc>
                          ))}
                      </Carousel>
                    )}
                </>
              )}
            </TagsHoc>
          );
        })}

        {filteredEvents.length <= 0 && (
          <div className="italic text-slate px-4 pt-4 text-center no-text-footer">
            {data.appConfig.NO_RESULTS_TEXT}
          </div>
        )}
      </div>
    </div>
  );
}

export default LineupPage;
